import {Icon} from '@wandb/components';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';

import {useAgentActionHandler} from './agentActionHandlers';
import {parseAgentActionArgs} from './agentActions';
import {AGENT_ACTIONS} from './knownActions';

// Align with the borderless tool-call rows (WBAgentStepDisclosure): full width,
// px-6 so the leading icon and trailing play icon land in the same columns
// as those rows' icon and expand chevron. A border frames the row (at the same
// -6 gutter the disclosure rows' hover background uses) to read as a button.
// my-4 keeps consecutive rows from butting together; py-8 makes it slightly
// taller than the other rows.
const ROW_CLASSES =
  'group my-4 flex w-full items-center gap-8 px-6 py-4 rounded-md border text-left text-sm leading-[21px] font-medium transition-colors';
const ENABLED_CLASSES =
  'cursor-pointer border-moon-250 text-moon-800 hover:bg-moon-100 hover:border-moon-350 dark:border-moon-650 dark:text-moon-200 dark:hover:bg-moon-800';
const DISABLED_CLASSES =
  'cursor-not-allowed border-moon-200 text-moon-400 dark:border-moon-700 dark:text-moon-500';

// Shown on hover when the action can't run on the current page.
const UNAVAILABLE_TOOLTIP = 'Action not currently available';

/** Prefer the agent-provided `label`; fall back to the action's derived caption. */
function resolveCaption(
  args: ReturnType<typeof parseAgentActionArgs>,
  fallback: (a: typeof args) => string
): string {
  const label = typeof args.label === 'string' ? args.label.trim() : '';
  return label || fallback(args);
}

export const AgentActionButton: React.FC<{name: string; argsText?: string}> = ({
  name,
  argsText,
}) => {
  const handler = useAgentActionHandler(name); // call before any early return (rules of hooks)
  const action = AGENT_ACTIONS[name];
  if (!action) {
    return null;
  }
  const args = parseAgentActionArgs(argsText);
  const caption = resolveCaption(args, action.label);
  const execute = handler ?? action.execute;
  const disabled = execute == null;
  return (
    <Tooltip
      content={disabled ? UNAVAILABLE_TOOLTIP : caption}
      trigger={
        <button
          type="button"
          disabled={disabled}
          onClick={disabled ? undefined : () => execute(args)}
          className={`${ROW_CLASSES} ${disabled ? DISABLED_CLASSES : ENABLED_CLASSES}`}>
          <span className="inline-flex shrink-0 items-center">
            <Icon
              icon={action.icon}
              size="sm"
              color={disabled ? 'moon' : 'teal'}
            />
          </span>
          <span className="flex min-w-0 flex-1 items-center gap-4">
            <span className="shrink-0 font-semibold">{action.name}:</span>
            <span className="min-w-0 truncate text-moon-500 dark:text-moon-450">
              {caption}
            </span>
          </span>
          <span className="inline-flex shrink-0 items-center text-moon-450">
            <Icon icon="play" size="sm" />
          </span>
        </button>
      }
    />
  );
};
